import React from 'react';
import './SkeletonLoaders.css';

/**
 * Skeleton loader components for better perceived performance
 * These replace spinning loading indicators with content-shaped placeholders
 */

// Base skeleton component with animation
const SkeletonBase = ({ className = '', style = {}, children, ...props }) => (
  <div 
    className={`skeleton-base ${className}`} 
    style={style}
    aria-hidden="true"
    {...props}
  >
    {children}
  </div>
);

// Simple skeleton elements
export const SkeletonText = ({ width = '100%', height = '1rem', className = '' }) => (
  <SkeletonBase 
    className={`skeleton-text ${className}`}
    style={{ width, height }}
  />
);

export const SkeletonAvatar = ({ size = '2.5rem', className = '' }) => (
  <SkeletonBase 
    className={`skeleton-avatar ${className}`}
    style={{ width: size, height: size }}
  />
);

export const SkeletonButton = ({ width = '6rem', height = '2rem', className = '' }) => (
  <SkeletonBase 
    className={`skeleton-button ${className}`}
    style={{ width, height }}
  />
);

// Order card skeleton (matches OrderCard component structure)
export const OrderCardSkeleton = ({ className = '' }) => (
  <div className={`skeleton-order-card ${className}`}>
    <div className="skeleton-order-header">
      <div className="skeleton-order-info">
        <SkeletonAvatar size="2rem" className="skeleton-provider-icon" />
        <div className="skeleton-order-details">
          <SkeletonText width="60%" height="1.1rem" className="skeleton-order-title" />
          <SkeletonText width="40%" height="0.9rem" className="skeleton-order-subtitle" />
        </div>
      </div>
      <div className="skeleton-order-badges">
        <SkeletonButton width="4rem" height="1.5rem" className="skeleton-status-badge" />
        <SkeletonButton width="3rem" height="1.5rem" className="skeleton-cost-badge" />
      </div>
    </div>
    
    <div className="skeleton-order-content">
      <SkeletonText width="80%" height="0.9rem" />
      <SkeletonText width="60%" height="0.9rem" />
      <SkeletonText width="45%" height="0.9rem" />
    </div>
    
    <div className="skeleton-order-footer">
      <div className="skeleton-order-meta">
        <SkeletonText width="8rem" height="0.8rem" />
        <SkeletonText width="6rem" height="0.8rem" />
      </div>
      <div className="skeleton-order-actions">
        <SkeletonButton width="4rem" height="1.8rem" />
        <SkeletonButton width="5rem" height="1.8rem" />
      </div>
    </div>
  </div>
);

// Job card skeleton (matches JobCard component structure)
export const JobCardSkeleton = ({ size = 'regular', className = '' }) => {
  const isCompact = size === 'compact';
  
  return (
    <div className={`skeleton-job-card ${isCompact ? 'compact' : 'regular'} ${className}`}>
      <div className="skeleton-job-header">
        <SkeletonAvatar 
          size={isCompact ? '1.5rem' : '2rem'} 
          className="skeleton-provider-icon" 
        />
        <div className="skeleton-job-info">
          <SkeletonText 
            width="70%" 
            height={isCompact ? '0.9rem' : '1rem'} 
            className="skeleton-job-title" 
          />
          {!isCompact && (
            <SkeletonText width="50%" height="0.8rem" className="skeleton-job-subtitle" />
          )}
        </div>
        <SkeletonButton 
          width={isCompact ? '3rem' : '4rem'} 
          height={isCompact ? '1.2rem' : '1.5rem'} 
          className="skeleton-status-badge" 
        />
      </div>
      
      {!isCompact && (
        <div className="skeleton-job-content">
          <SkeletonText width="90%" height="0.8rem" />
          <SkeletonText width="70%" height="0.8rem" />
        </div>
      )}
      
      <div className="skeleton-job-footer">
        <SkeletonText width="6rem" height="0.7rem" />
        <SkeletonText width="4rem" height="0.7rem" />
        {!isCompact && <SkeletonText width="5rem" height="0.7rem" />}
      </div>
    </div>
  );
};

// Results list skeleton (multiple order cards)
export const ResultsListSkeleton = ({ count = 3, className = '' }) => (
  <div className={`skeleton-results-list ${className}`}>
    {Array.from({ length: count }, (_, i) => (
      <OrderCardSkeleton key={i} />
    ))}
  </div>
);

// Jobs list skeleton (multiple job cards)
export const JobsListSkeleton = ({ count = 5, size = 'regular', className = '' }) => (
  <div className={`skeleton-jobs-list ${className}`}>
    {Array.from({ length: count }, (_, i) => (
      <JobCardSkeleton key={i} size={size} />
    ))}
  </div>
);

// Breadcrumb skeleton
export const BreadcrumbSkeleton = ({ items = 3, className = '' }) => (
  <div className={`skeleton-breadcrumb ${className}`}>
    {Array.from({ length: items }, (_, i) => (
      <React.Fragment key={i}>
        <SkeletonText 
          width={`${Math.random() * 40 + 60}px`} 
          height="1rem" 
          className="skeleton-breadcrumb-item" 
        />
        {i < items - 1 && (
          <span className="skeleton-breadcrumb-separator">›</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

// Modal header skeleton
export const ModalHeaderSkeleton = ({ className = '' }) => (
  <div className={`skeleton-modal-header ${className}`}>
    <div className="skeleton-header-content">
      <BreadcrumbSkeleton items={2} />
      <div className="skeleton-header-actions">
        <SkeletonButton width="3rem" height="2rem" />
        <SkeletonButton width="4rem" height="2rem" />
        <SkeletonButton width="2rem" height="2rem" />
      </div>
    </div>
  </div>
);

// Complete modal skeleton (for initial loading)
export const ModalSkeleton = ({ 
  showHeader = true, 
  orderCount = 3, 
  className = '' 
}) => (
  <div className={`skeleton-modal ${className}`}>
    {showHeader && <ModalHeaderSkeleton />}
    <div className="skeleton-modal-content">
      <ResultsListSkeleton count={orderCount} />
    </div>
  </div>
);

// Inline results skeleton (for expanded job results)
export const InlineResultsSkeleton = ({ className = '' }) => (
  <div className={`skeleton-inline-results ${className}`}>
    <div className="skeleton-tabs">
      {['Summary', 'Vehicles', 'People', 'Text', 'Timeline'].map((tab, i) => (
        <SkeletonButton 
          key={tab} 
          width="4rem" 
          height="2rem" 
          className={`skeleton-tab ${i === 0 ? 'active' : ''}`} 
        />
      ))}
    </div>
    
    <div className="skeleton-tab-content">
      <SkeletonText width="100%" height="1rem" />
      <SkeletonText width="85%" height="1rem" />
      <SkeletonText width="70%" height="1rem" />
      
      <div className="skeleton-results-grid">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="skeleton-result-item">
            <SkeletonAvatar size="1.5rem" />
            <div className="skeleton-result-content">
              <SkeletonText width="80%" height="0.9rem" />
              <SkeletonText width="60%" height="0.8rem" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Provider tabs skeleton
export const ProviderTabsSkeleton = ({ providerCount = 3, className = '' }) => (
  <div className={`skeleton-provider-tabs ${className}`}>
    {Array.from({ length: providerCount }, (_, i) => (
      <div key={i} className={`skeleton-provider-tab ${i === 0 ? 'active' : ''}`}>
        <SkeletonAvatar size="1.5rem" className="skeleton-provider-icon" />
        <SkeletonText width="4rem" height="1rem" />
        <SkeletonButton width="1.5rem" height="1.2rem" className="skeleton-job-count" />
      </div>
    ))}
  </div>
);
